import React from 'react';
import { Icon } from '@heartinternet/hi-ui';
import type { Package, Permissions } from '../types';
import { DropdownMenu } from '../../../components/DropdownMenu';

export interface ManagePackageMenuProps {
  row: Package;
  permissions: Permissions;
  noExtendRelogin?: boolean;
}

/**
 * Row-actions cog dropdown for a hosting package on the Manage Packages page. Open/close,
 * hover-bridge and on-screen positioning come from the shared DropdownMenu component; this
 * component supplies the page's markup and the permission-gated link items (Edit, Control
 * Panel, Manage Domain). Renders nothing while a migration is in progress.
 */
export const ManagePackageMenu: React.FC<ManagePackageMenuProps> = ({
  row,
  permissions,
  noExtendRelogin = false,
}) => {
  if (row.migration?.status === 'started') return null;

  return (
    <DropdownMenu
      openOn="hover"
      className="hi-ui-manage-packages__cog-wrapper"
      openClassName="hi-ui-manage-packages__cog-wrapper--open"
      menuClassName="hi-ui-manage-packages__dropdown"
      renderTrigger={({ ref, toggle }) => (
        <button
          ref={ref}
          className="hi-ui-manage-packages__cog-trigger"
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-label="Package options"
        >
          <Icon name="cog" size={20} />
        </button>
      )}
    >
      {() => (
        <>
          {/* eslint-disable react/jsx-no-target-blank -- same-origin links; noreferrer triggers server redirect page */}
          {permissions.config && (
            <a
              href={`/manage/manage-package-details.cgi?id=${row.oid}`}
              target="_blank"
              rel="noopener"
              className="hi-ui-manage-packages__dropdown-item"
            >
              Edit
            </a>
          )}
          {row.login && (
            <>
              <a
                href={`/manage/${noExtendRelogin ? 'manage_hosting' : 'package_view'}.cgi/${row.oid}`}
                target="_blank"
                rel="noopener"
                className="hi-ui-manage-packages__dropdown-item"
              >
                Control Panel (Customer)
              </a>
              <a
                href={`/manage/${noExtendRelogin ? 'manage_hosting' : 'package_view'}.cgi/${row.oid}/superuser`}
                target="_blank"
                rel="noopener"
                className="hi-ui-manage-packages__dropdown-item"
              >
                Control Panel (Superuser)
              </a>
            </>
          )}
          {row.primary_domain_oid && (
            <a
              href={`/domains/${row.primary_domain_oid}/index.cgi?domain=${row.primary_domain_oid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hi-ui-manage-packages__dropdown-item"
            >
              Manage Domain (DomainCP)
            </a>
          )}
        </>
      )}
    </DropdownMenu>
  );
};
